import React, { useState, useEffect } from 'react';
import { usePostgresUser } from '../components/utils/PostgresUserContext';
import Footer from '../components/footer';
import Navbar from '../components/navBar';
import { ClipLoader } from 'react-spinners';
import { useNavigate } from 'react-router-dom';
import { backendService } from '../services/api/backendService';
import { Helmet } from 'react-helmet';

import pricingFreeImage from '../images/landingPageAssets/onikanji-free-tier.png';
import pricingFishImage from '../images/pricingAssets/onikanji-monthly-sub.png';
import pricingBonsaiImage from '../images/pricingAssets/onikanji-yearly-sub.png';
import pricingOniMaskImage from '../images/pricingAssets/onikanji-lifetime-sub.png';

function OnikanjiPricing() {
    const { user, loading: userLoading, subscriptionStatus, subscriptionName, firebaseUser } = usePostgresUser();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isManageSubscriptionLoading, setIsManageSubscriptionLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        // Title/meta managed by <Helmet> below (removed a racy document.title set).

        // TODO: Replace with backend API endpoint for subscription plans
        // For now, use hardcoded product data since Firestore is being removed
        const loadProducts = () => {
            try {
                const hardcodedProducts = [
                    {
                        id: 'monthly',
                        name: 'OniKanji Monthly Subscription',
                        active: true,
                        prices: [{ id: 'price_1Q6CB205vwFjJgoTF4aSUbiN', recurring: true, unit_amount: 900 }]
                    },
                    {
                        id: 'yearly',
                        name: 'OniKanji Yearly Subscription',
                        active: true,
                        prices: [{ id: 'price_1TAAHL05vwFjJgoT8Yr1npyv', recurring: true, unit_amount: 6000 }]
                    },
                    {
                        id: 'lifetime',
                        name: 'OniKanji Lifetime Membership',
                        active: true,
                        prices: [{ id: 'price_1TAAKv05vwFjJgoT5u7Xi8vt', recurring: false, unit_amount: 24900 }]
                    }
                ];
                setProducts(hardcodedProducts);
            } catch (error) {
                console.error("Error loading products:", error);
            } finally {
                setLoading(false);
            }
        };

        loadProducts();

        return () => {
            setLoading(false);
        };
    }, []);


    // Add loading screen
    if (loading || userLoading) {
        return (
            <div className="min-h-screen flex justify-center items-center bg-mainBackground">
                <ClipLoader size={50} color="#014156" />
            </div>
        );
    }

    const handleSubscribe = async (product) => {
        if (!user) {
            navigate('/signup');
            return;
        }

        setLoading(true);

        try {
            const idToken = await firebaseUser?.getIdToken();
            if (!idToken) {
                navigate('/login');
                return;
            }

            const priceId = product.prices[0].id;
            const result = await backendService.createSubscriptionCheckout(
                priceId,
                `${window.location.origin}/home?subscription=success`,
                `${window.location.origin}/pricing?subscription=cancelled`,
                idToken
            );

            // Redirect to Stripe hosted checkout
            window.location.href = result.url;
        } catch (error) {
            console.error("Error creating checkout session:", error);
            alert("An error occurred. Please try again.");
            setLoading(false);
        }
    };

    const handleManageSubscription = async () => {
        setIsManageSubscriptionLoading(true);
        try {
            const idToken = await firebaseUser?.getIdToken();
            if (!idToken) {
                navigate('/login');
                return;
            }

            const result = await backendService.createPortalSession(
                `${window.location.origin}/pricing`,
                idToken
            );

            window.location.href = result.url;
        } catch (error) {
            console.error("Error creating portal link:", error);
            alert("An error occurred. Please try again.");
        } finally {
            setIsManageSubscriptionLoading(false);
        }
    };

    const getButtonText = (productName) => {
        if (subscriptionStatus === 'active') {
            if (subscriptionName === productName) {
                return 'Manage';
            } else if (productName === 'OniKanji Lifetime Membership') {
                return 'One Time Purchase';
            } else {
                return 'Upgrade Now';
            }
        }
        return 'Subscribe Now';
    };

    // Identify products by name instead of fragile index
    const getProductType = (name) => {
        if (name?.includes('Monthly')) return 'monthly';
        if (name?.includes('Yearly')) return 'yearly';
        if (name?.includes('Lifetime')) return 'lifetime';
        return 'unknown';
    };

    return (
        <div className="font-noto-sans-jp">
            <Helmet>
                <title>OniKanji Pricing — Free Kanji Lessons, Monthly, Yearly &amp; Lifetime Plans</title>
                <meta name="description" content="See OniKanji pricing. Start free with the first three levels, then pick a monthly, yearly, or lifetime plan for spaced-repetition kanji lessons, level rewards, and the Bring Your Own Manga reader." />
                <link rel="canonical" href="https://onikanji.com/pricing" />
                <meta property="og:title" content="OniKanji Pricing — Plans for Every Kanji Learner" />
                <meta property="og:description" content="Free to start. Monthly, yearly, and lifetime plans for spaced-repetition kanji study and reading the manga you own." />
                <meta property="og:image" content="https://onikanji.com/og-image.png" />
                <meta property="og:url" content="https://onikanji.com/pricing" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="OniKanji" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="OniKanji Pricing — Plans for Every Kanji Learner" />
                <meta name="twitter:description" content="Free to start. Monthly, yearly, and lifetime plans for spaced-repetition kanji study and reading the manga you own." />
                <meta name="twitter:image" content="https://onikanji.com/og-image.png" />
            </Helmet>
            <Navbar user={user} subscriptionStatus={subscriptionStatus} />
            <div className="w-full py-8 sm:py-12 md:py-20 px-4 sm:px-6 md:px-8 bg-mainBackground">
                <div className="max-w-7xl mx-auto">
                    <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-logoColor mb-6 sm:mb-8 md:mb-12 text-center leading-tight">
                        {subscriptionStatus === 'active' ? 'Upgrade or Manage Your Subscription' : 'Choose Your Learning Path'}
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 md:gap-8 max-w-7xl mx-auto">

                        {/* FREE TIER CARD */}
                        <div className="relative bg-white rounded-2xl shadow-blueBoxShadow border border-gray-200 p-4 sm:p-6 md:p-8 flex flex-col h-full transition-all duration-300 hover:shadow-xl hover:scale-105 transform">
                            <div className="mb-6 max-sm:mb-3 sm:max-lg:mb-3">
                                <div className="aspect-w-16 aspect-h-9 relative overflow-hidden rounded-xl">
                                    <img
                                        src={pricingFreeImage}
                                        alt="Free Plan"
                                        className="w-full h-full max-sm:h-36 sm:max-lg:h-44 object-contain rounded-xl transition-transform duration-300 hover:scale-110"
                                    />
                                </div>
                            </div>
                            <h3 className="text-2xl md:text-3xl font-bold text-logoColor mb-6 max-sm:mb-3 max-sm:text-xl sm:max-lg:!text-2xl sm:max-lg:mb-3 text-center">OniKanji Free Subscription</h3>
                            <div className="text-center mb-6">
                                <p className="text-5xl md:text-6xl font-bold text-logoColor mb-2">$0</p>
                                <p className="text-lg text-gray-600 mb-3">forever</p>
                                <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                                    <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                    No credit card required
                                </div>
                            </div>
                            <div className="mb-6 sm:mb-8 flex-grow">
                                <ul className="space-y-3 mb-4">
                                    <li className="flex items-center text-sm text-gray-700">
                                        <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                        First 3 Levels (60 Kanji)
                                    </li>
                                    <li className="flex items-center text-sm text-gray-700">
                                        <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                        Full Access to All Kanji Flashcards
                                    </li>
                                    <li className="flex items-center text-sm text-gray-700">
                                        <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                        Level End Manga Rewards
                                    </li>
                                    <li className="flex items-center text-sm text-gray-700">
                                        <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                        Extra Study Practice
                                    </li>
                                    <li className="flex items-center text-sm text-gray-700">
                                        <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                        Discord Reading Events
                                    </li>
                                    <li className="flex items-center text-sm text-gray-700">
                                        <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                        Kana Review
                                    </li>
                                    <li className="flex items-center text-sm text-gray-700">
                                        <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                        Leaderboards
                                    </li>
                                </ul>
                            </div>
                            <button
                                onClick={() => {
                                    navigate(user ? '/home' : '/signup');
                                }}
                                className="w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg bg-gradient-to-r from-logoColor to-darkBlueColor hover:from-darkBlueColor hover:to-logoColor text-white flex items-center justify-center"
                            >
                                {user ? 'Go to Dashboard' : 'Start Free'}
                            </button>
                        </div>

                        {/* PAID TIER CARDS */}
                        {products.map((product) => {
                            const type = getProductType(product.name);
                            const isYearly = type === 'yearly';
                            const isLifetime = type === 'lifetime';
                            const isPopular = isYearly;

                            const image = type === 'monthly' ? pricingFishImage :
                                          type === 'yearly' ? pricingBonsaiImage :
                                          pricingOniMaskImage;

                            return (
                            <div key={product.id} className={`relative bg-white rounded-2xl shadow-blueBoxShadow border ${isPopular ? 'border-2 border-highContrastOrange bg-gradient-to-b from-orange-50 to-white' : isLifetime ? 'border-2 border-darkOrangeColor bg-gradient-to-b from-lightOrangeBackground to-white' : 'border border-gray-200'} p-4 sm:p-6 md:p-8 flex flex-col h-full transition-all duration-300 hover:shadow-xl hover:scale-105 transform`}>
                                {/* Popular Badge - on Yearly */}
                                {isPopular && (
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                                        <span className="bg-highContrastOrange text-white px-3 py-1 sm:px-4 sm:py-1 rounded-full text-xs sm:text-sm font-bold shadow-lg">
                                            Most Popular
                                        </span>
                                    </div>
                                )}

                                {/* Lifetime Badge */}
                                {isLifetime && (
                                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                                        <span className="bg-darkOrangeColor text-white px-3 py-1 sm:px-4 sm:py-1 rounded-full text-xs sm:text-sm font-bold shadow-lg">
                                            Best Value
                                        </span>
                                    </div>
                                )}

                                <div className="mb-6 max-sm:mb-3 sm:max-lg:mb-3">
                                    <div className="aspect-w-16 aspect-h-9 relative overflow-hidden rounded-xl">
                                        <img
                                            src={image}
                                            alt={`${product.name} Plan`}
                                            className="w-full h-full max-sm:h-36 sm:max-lg:h-44 object-contain rounded-xl transition-transform duration-300 hover:scale-110"
                                        />
                                    </div>
                                </div>
                                <h3 className="text-2xl md:text-3xl font-bold text-logoColor mb-6 max-sm:mb-3 max-sm:text-xl sm:max-lg:!text-2xl sm:max-lg:mb-3 text-center">{product.name}</h3>

                                {/* Monthly pricing */}
                                {type === 'monthly' && (
                                    <div className="text-center mb-6">
                                        <div className="relative">
                                            <p className="text-5xl md:text-6xl font-bold text-logoColor mb-2">$9<span className="text-2xl font-normal">.00</span></p>
                                            <p className="text-lg text-gray-600 mb-3">per month</p>
                                            <div className="inline-flex items-center px-3 py-1 rounded-full bg-green-100 text-green-800 text-sm font-medium">
                                                <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                                                Most flexible
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Yearly pricing */}
                                {isYearly && (
                                    <div className="text-center mb-6">
                                        <div className="relative">
                                            <p className="text-3xl md:text-4xl font-bold text-logoColor mb-1">$5<span className="text-xl font-normal">.00</span><span className="text-base text-gray-500">/month</span></p>
                                            <p className="text-5xl md:text-6xl font-bold text-logoColor mb-2">$60<span className="text-2xl font-normal">.00</span></p>
                                            <p className="text-lg text-gray-600 mb-3">billed annually</p>
                                            <div className="inline-flex items-center px-3 py-1 rounded-full bg-logoColor text-white text-sm font-medium">
                                                Save $48/year
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Lifetime pricing */}
                                {isLifetime && (
                                    <div className="text-center mb-6">
                                        <div className="relative">
                                            <p className="text-5xl md:text-6xl font-bold text-darkOrangeColor mb-2">$249<span className="text-2xl font-normal">.00</span></p>
                                            <p className="text-lg text-gray-600 mb-3">one-time payment</p>
                                            <div className="inline-flex items-center px-3 py-1 rounded-full bg-yellow-100 text-yellow-800 text-sm font-medium">
                                                Yours forever
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className="mb-6 sm:mb-8 flex-grow">
                                    {type === 'monthly' ? (
                                        <>
                                            <div className="text-center mb-4">
                                                <p className="text-sm text-gray-600 font-medium">Cancel anytime</p>
                                            </div>
                                            <ul className="space-y-3 mb-4">
                                                <li className="flex items-center text-sm text-gray-700">
                                                    <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                                    Full Kanji Curriculum
                                                </li>
                                                <li className="flex items-center text-sm text-gray-700">
                                                    <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                                    Unlimited Extra Study
                                                </li>
                                                <li className="flex items-center text-sm text-gray-700">
                                                    <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                                    New Features First
                                                </li>
                                                <li className="flex items-center text-sm text-gray-700">
                                                    <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                                    Premium Question Types
                                                </li>
                                                <li className="flex items-center text-sm text-gray-700">
                                                    <span className="text-logoColor mr-2 font-bold">&#10003;</span>
                                                    Discord Roles
                                                </li>
                                            </ul>
                                            <div className="text-center">
                                                <div className="inline-flex items-center px-3 py-2 bg-highContrastOrange bg-opacity-10 text-highContrastOrange rounded-full text-sm font-medium">
                                                    Cancel anytime
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <>
                                            <div className="text-center mb-4">
                                                <p className="text-sm text-gray-600 font-medium">
                                                    {isYearly ? "Best value for serious learners" : "One-time payment forever"}
                                                </p>
                                            </div>
                                            <div className="text-center mb-4">
                                                <p className="text-sm text-gray-500">Everything in Monthly +</p>
                                            </div>
                                            <div className="text-center space-y-2">
                                                {isYearly && (
                                                    <>
                                                        <div className="inline-flex items-center px-3 py-2 bg-immersionHubGreen bg-opacity-10 text-immersionHubGreen rounded-full text-sm font-medium">
                                                            Save $48/year
                                                        </div>
                                                        <div className="inline-flex items-center px-3 py-2 bg-immersionHubGreen bg-opacity-10 text-immersionHubGreen rounded-full text-sm font-medium">
                                                            500 free coins
                                                        </div>
                                                    </>
                                                )}
                                                {isLifetime && (
                                                    <>
                                                        <div className="inline-flex items-center px-3 py-2 bg-immersionHubGreen bg-opacity-10 text-immersionHubGreen rounded-full text-sm font-medium">
                                                            1,000 free coins
                                                        </div>
                                                        <div className="inline-flex items-center px-3 py-2 bg-darkOrangeColor bg-opacity-10 text-darkOrangeColor rounded-full text-sm font-medium">
                                                            Yours forever
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </>
                                    )}
                                </div>
                                {subscriptionStatus === 'active' && subscriptionName === product.name ? (
                                    <button
                                        onClick={handleManageSubscription}
                                        className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg ${isLifetime ? 'bg-gradient-to-r from-darkOrangeColor to-highContrastOrange hover:from-highContrastOrange hover:to-darkOrangeColorButtonHover text-white' : isPopular ? 'bg-gradient-to-r from-highContrastOrange to-highContrastOrangeHover hover:from-highContrastOrangeHover hover:to-highContrastOrange text-white' : 'bg-gradient-to-r from-logoColor to-darkBlueColor hover:from-darkBlueColor hover:to-logoColor text-white'} flex items-center justify-center`}
                                        disabled={isManageSubscriptionLoading}
                                    >
                                        {isManageSubscriptionLoading ? (
                                            <>
                                                <ClipLoader size={20} color="white" className="mr-2" />
                                                Processing...
                                            </>
                                        ) : (
                                            getButtonText(product.name)
                                        )}
                                    </button>
                                ) : (
                                    <>
                                        {subscriptionStatus === 'active' &&
                                         product.name === 'OniKanji Lifetime Membership' &&
                                         (subscriptionName === 'OniKanji Monthly Subscription' ||
                                          subscriptionName === 'OniKanji Yearly Subscription') && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                                                <p className="text-sm text-blue-700 text-center font-medium">
                                                    <span className="block mb-1">Seamless Upgrade</span>
                                                    Your current subscription will be automatically cancelled after purchasing lifetime access
                                                </p>
                                            </div>
                                        )}
                                        <button
                                            onClick={() => handleSubscribe(product)}
                                            className={`w-full py-3 sm:py-4 px-4 sm:px-6 rounded-xl font-bold text-base sm:text-lg transition-all duration-300 transform hover:scale-105 shadow-lg ${isLifetime ? 'bg-gradient-to-r from-darkOrangeColor to-highContrastOrange hover:from-highContrastOrange hover:to-darkOrangeColorButtonHover text-white' : isPopular ? 'bg-gradient-to-r from-highContrastOrange to-highContrastOrangeHover hover:from-highContrastOrangeHover hover:to-highContrastOrange text-white' : 'bg-gradient-to-r from-logoColor to-darkBlueColor hover:from-darkBlueColor hover:to-logoColor text-white'} flex items-center justify-center`}
                                            disabled={loading}
                                        >
                                            {loading ? (
                                                <ClipLoader color="white" size={24} />
                                            ) : (
                                                getButtonText(product.name)
                                            )}
                                        </button>
                                    </>
                                )}
                            </div>
                            );
                        })}
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

export default OnikanjiPricing;
